import { useEffect, useState } from 'react';

import { useIsRealSupport, useIsSupport } from '../Auth/rbac/useRole';
import usePlanStore from '../store/plan';
import { type Feature, isFeatureInPlan } from './features';
import { promoteStarterPlanOverrideFromUrl } from '../utils/previewOverrideUrl';
import { STARTER_PLAN_OVERRIDE_EVENT, getStarterPlanOverride } from './starterPreviewOverride';
import type { Plan } from './plans';

/**
 * Three states, not a boolean. `loading` is what stops an upsell screen flashing over
 * a feature the team actually owns while the team query is still in flight.
 */
export type EntitlementState = 'loading' | 'granted' | 'denied';

/**
 * The support/dev override, live. `null` when no override is set — and always `null` for
 * anyone who is not really Nexoya support.
 *
 * The gate is `useIsRealSupport`, never `useIsSupport`: the latter reads the EFFECTIVE
 * role, which the role preview can rewrite, so the override would be authorising its own
 * bypass. The Account switch that sets this is gated on the real role too; an ungated
 * reader would make that gate decoration, since `?preview_plan=…` alone rewrites the plan
 * every entitlement check below reads.
 *
 * `useSyncExternalStore` would be the idiomatic hook, but this repo's `@types/react` is
 * pinned at 17 while React 18.3 is installed, so it does not type-check. Subscribe by
 * hand instead.
 */
export function useStarterPlanOverride(): Plan | null {
  const isRealSupport = useIsRealSupport();
  const [override, setOverride] = useState<Plan | null>(getStarterPlanOverride);

  useEffect(() => {
    // Ignore both sources outright for anyone else — no read, and above all no promote,
    // which would persist a stranger's URL param into their own storage.
    if (!isRealSupport) return;

    const sync = () => {
      // Consume the URL param first so localStorage is the single live source
      // afterwards; otherwise the override can never be switched off. It is a no-op once
      // the param is gone, so re-running it per event is free. See the function's own
      // comment.
      promoteStarterPlanOverrideFromUrl();
      setOverride(getStarterPlanOverride());
    };
    // Re-read once on mount: the value can change between first render and subscribing.
    sync();
    // `storage` fires in OTHER tabs; the CustomEvent covers the tab that made the change.
    window.addEventListener('storage', sync);
    window.addEventListener(STARTER_PLAN_OVERRIDE_EVENT, sync);
    // Back/forward can land on an older entry that still carries the param — only the
    // entry we replaced was cleaned — so promote and re-resolve on `popstate` too.
    // `useLocation()` would be the obvious alternative and is not usable: this hook has
    // to work outside a Router (Storybook, unit tests).
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(STARTER_PLAN_OVERRIDE_EVENT, sync);
      window.removeEventListener('popstate', sync);
    };
    // Re-runs when the user store resolves: the role is null on the very first paint.
  }, [isRealSupport]);

  // Guarded on render, not just in the effect, so a non-support user never gets even one
  // frame of an overridden plan out of the `useState` initialiser.
  return isRealSupport ? override : null;
}

/**
 * Composes the two axes. Neither `FEATURES` nor `PERMISSIONS` knows about the other —
 * this is the only place they meet.
 *
 * Support bypass is checked first and deliberately covers the `loading` state too:
 * Nexoya staff must never be shown an upsell inside a customer team, not even for a
 * frame. It reads the *effective* role, so entering presenter mode drops the bypass
 * along with every other privilege — which is the point of presenter mode.
 */
export function useEntitlement(feature: Feature | null): EntitlementState {
  const isSupport = useIsSupport();
  const override = useStarterPlanOverride();
  const stored = usePlanStore((s) => s.plan);
  const plan = override ?? stored;

  // `null` = this surface is not plan-gated at all. Resolving it here rather than at the
  // call site keeps callers free of conditional hooks.
  if (feature === null) return 'granted';

  // An active preview override suspends the support bypass. Without this the "Preview as
  // Nexoya Starter" toggle would be inert for the only people who can see it.
  if (isSupport && override === null) return 'granted';
  if (plan === null) return 'loading';
  return isFeatureInPlan(feature, plan) ? 'granted' : 'denied';
}

/** Convenience for the common `=== 'denied'` branch. `loading` is NOT denied. */
export function useIsFeatureLocked(feature: Feature | null): boolean {
  return useEntitlement(feature) === 'denied';
}

/**
 * The team's plan, or `null` while unresolved.
 *
 * For surfaces that branch on the *tier* rather than on a single capability — the Nexoya
 * Starter onboarding roster, its redirect, its sidebar entry. Anything gating one
 * capability should use `useEntitlement` instead, which also carries the support bypass.
 *
 * Composed exactly as `useEntitlement` composes it above. `useEntitlement` is deliberately
 * not refactored to call this: it needs the override *separately* from the resolved plan
 * for its bypass rule, so sharing would mean subscribing to the override twice.
 * `usePlan.component.test.tsx` asserts the two stay in agreement instead.
 */
export function usePlan(): Plan | null {
  const override = useStarterPlanOverride();
  const stored = usePlanStore((s) => s.plan);
  return override ?? stored;
}
